import React from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Router } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { PersistGate } from 'redux-persist/integration/react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ScrollRestoration from 'react-scroll-restoration';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { useClearCache } from 'react-clear-cache';
import moment from 'moment-timezone';

import ApolloWithOAuth from '@services/ApolloWithOAuth';

import { store, persistor } from '@store';
import { routes } from '@router';
import mainTheme from '@styled/themes/mainTheme';
import GlobalStyle from '@styled/globalStyle';
import history from '@utils/history';
import EnglishTranslation from '@utils/location/en';
import FranceTranslation from '@utils/location/fr';
import { getLanguage, getFirebaseConfig } from '@utils/config';
import { isPre, isProd } from '@config/getEnviroment';
import { MenuProvider } from '@layouts/common/Menu';
import Notifications from '@components/Notifications';
import ErrorBoundary from '@components/ErrorBoundary';

moment.tz.setDefault('Europe/Paris');
i18n.use(initReactI18next).init({
  resources: {
    en: {
      ...EnglishTranslation,
    },
    fr: {
      ...FranceTranslation,
    },
  },
  lng: getLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});
const isPrePro = isPre();
const isProduction = isProd();
if (isPrePro || isProduction) {
  const app = initializeApp(getFirebaseConfig(isPrePro));
  getAnalytics(app);
}

const App = () => {
  const { isLatestVersion, emptyCacheStorage } = useClearCache();

  if (!isLatestVersion) {
    return emptyCacheStorage();
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ApolloWithOAuth>
            <ThemeProvider theme={mainTheme}>
              <GlobalStyle />
              <Notifications />
              <MenuProvider>
                <Router history={history}>
                  <ScrollRestoration />
                  <React.Suspense fallback={<p />}>{renderRoutes(routes)}</React.Suspense>
                </Router>
              </MenuProvider>
            </ThemeProvider>
          </ApolloWithOAuth>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
